// Admin chat alerts: soft chime + tab-title flash when a guest writes during a takeover (needs one user interaction to unlock audio).
import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'rn_chat_sound';

export const chime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [[880, 0], [1320, 0.12]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.18, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch { /* audio blocked */ }
};

// Flash the document title while `count` > 0 and the tab is hidden or unfocused.
export const useTitleFlash = (count) => {
  useEffect(() => {
    if (!count) return undefined;
    const original = document.title;
    let on = false;
    const id = setInterval(() => {
      on = !on;
      document.title = on ? `(${count}) Guest waiting… · La Rosa Negra` : original;
    }, 1000);
    const stop = () => { clearInterval(id); document.title = original; };
    window.addEventListener('focus', stop, { once: true });
    return () => { clearInterval(id); document.title = original; window.removeEventListener('focus', stop); };
  }, [count]);
};

// Chime once per newly-waiting session (by session id), respecting the mute toggle.
export const useChatChime = (unansweredSessions) => {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) !== 'off');
  const seen = useRef(null);
  useEffect(() => {
    const ids = new Set((unansweredSessions || []).map((s) => s.session_id));
    if (seen.current === null) { seen.current = ids; return; } // first load: no chime for what's already waiting
    const fresh = [...ids].filter((id) => !seen.current.has(id));
    seen.current = ids;
    if (fresh.length && enabled) chime();
  }, [unansweredSessions, enabled]);
  const toggle = () => setEnabled((v) => { localStorage.setItem(STORAGE_KEY, v ? 'off' : 'on'); return !v; });
  return { enabled, toggle };
};
